import { Injectable, Injector } from '@angular/core';
import moment from 'moment';
import "moment/locale/ja";
import { BehaviorSubject } from 'rxjs';
import { LOCAL_STORAGE_KEY, SESSION_STORAGE_KEY } from '@core/configs';
import { SessionStorageService } from './session-storage.service';
import { DeviceLogsService, DeviceRegistrationsService } from '@core/services/apis';
import { LocalStorageService } from './local-storage.service';
import { FirebaseDatabaseService } from './firebase/firebase-database.service';
import { DeviceLicensesService } from '@core/services/apis/device-licenses.service';
import { NativeDiagnosticLogService } from '../diagnostic-log/native-diagnostic-log.service';

// const HEARTBEAT_INTERVAL_MS = 60 * 60 * 1000; // 1 hour
const HEARTBEAT_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes

@Injectable({
  providedIn: 'root'
})
export class TimeService {
  private fakeTime :any = null;
  private currentTime:any = null;
  private currentTimeStrSubject$ = new BehaviorSubject<string>("");
  private currentTimeSubject$ = new BehaviorSubject<any>(null);
  private minuteAgoSubject$ = new BehaviorSubject<any>(0);
  private timeSyncFireBase:any = null;
  private lastHeartbeatTime: number = Date.now();
  private isHeartbeatInProgress = false;

  constructor(
    private readonly deviceRegistrationsService: DeviceRegistrationsService,
    // private readonly deviceLogsService: DeviceLogsService,
    private readonly sessionStorageService: SessionStorageService,
    private readonly localStorageService: LocalStorageService,
    private readonly deviceLicensesService: DeviceLicensesService,
    private readonly nativeDiagnosticLogService: NativeDiagnosticLogService,
    private readonly injector: Injector
  ) {
    this.fakeTime= new Date();
    this.currentTime = new Date();
    this.updateTime();
  }

  offset = 64000;
  isFakeTime = false;

  getCurrentTimeStrSubject() {
    return this.currentTimeStrSubject$.asObservable();
  }

  getCurrentTimeSubject() {
    return this.currentTimeSubject$.asObservable();
  }

  getMinuteAgoSubject() {
    return this.minuteAgoSubject$.asObservable();
  }

  getCurrentTime() {
    return this.currentTime;
  }
  
  setTimeSyncFireBase(time:any){
    this.timeSyncFireBase = time;
  }

  private updateTime() {
    try {

      if(this.isFakeTime) {
        this.fakeTime.setTime(this.fakeTime.getTime()+this.offset)//6000)
        this.currentTime = this.fakeTime;
      }else {
        this.currentTime = new Date();;//this.fakeTime;//new Date();
      }

      // Reuse a single moment instance for both dayJP and newTime to avoid creating 2 objects
      const currentMoment = moment(this.currentTime).locale("ja");
      const dayJP = currentMoment.format("ddd");
      const newTime = currentMoment.format(`YYYY-MM-DD [${dayJP}] HH:mm`);
      
      if (newTime !== this.currentTimeStrSubject$.value) { 
        this.currentTimeStrSubject$.next(newTime);
        // Only emit currentTimeSubject$ when the displayed minute actually changes (not every 100ms)
        this.currentTimeSubject$.next(this.currentTime);
      }
      
      if (this.timeSyncFireBase) {
        const lastSyncTime = moment(this.timeSyncFireBase);
        if (!lastSyncTime.isValid()) {
          // Skip only the elapsed-time calculation; heartbeat must keep running.
        } else {
          const diffSeconds = moment().diff(lastSyncTime, 'seconds');
          const diffMinutes = Math.floor(diffSeconds / 60);
          const minuteAgo = diffMinutes <= 0 ? 0 : diffMinutes;
          if(minuteAgo != this.minuteAgoSubject$.value) this.minuteAgoSubject$.next(minuteAgo);
        }
      }

      if (!this.isHeartbeatInProgress && Date.now() - this.lastHeartbeatTime >= HEARTBEAT_INTERVAL_MS) {
        this.lastHeartbeatTime = Date.now();
        this.sendHeartbeat();
      }
    } catch (error) {
      console.error("Error in updateTime:", error);
    } finally {
      setTimeout(() => this.updateTime(), 100);
    }
  }

  private async sendHeartbeat(): Promise<void> {
    this.isHeartbeatInProgress = true;
    try {
      const corpPrefix = this.localStorageService.getItem(LOCAL_STORAGE_KEY.corpPrefix) || "";
      const officeSelected = this.sessionStorageService.getSessionData(SESSION_STORAGE_KEY.officeSelected);
      const officeCd = officeSelected?.officeCd;

      if (!corpPrefix || !officeCd) return;

      // await this.deviceLogsService.saveDeviceLog(corpPrefix, officeCd);

      // Keep Firebase auth/listeners/device presence healthy before the backend heartbeat.
      const firebaseDb = this.injector.get(FirebaseDatabaseService);
      const firebaseHealthy = await firebaseDb.checkFirebaseHealth();
      if (!firebaseHealthy) return;

      await this.checkLogEnabled();

      const deviceToken = this.sessionStorageService.getSessionData(SESSION_STORAGE_KEY.deviceToken);
      if (!deviceToken) return;

      await this.deviceLicensesService.syncDeviceMetadata();

      const res = await this.deviceRegistrationsService.updateHeartbeat(
        corpPrefix,
        officeCd,
        deviceToken
      ).toPromise();

      // Backend detected the device is out of sync (ckDataChangedAt mismatch) → force reload latest data.
      if (res?.result === 'needsDataSync') {
        this.sessionStorageService.removeSessionData(SESSION_STORAGE_KEY.ckDataChangedByDevice);
        firebaseDb.triggerDataSync();
      }

    } catch (error) {
      console.warn('Device heartbeat failed:', error);
    } finally {
      this.isHeartbeatInProgress = false;
    }
  }

  private async checkLogEnabled(): Promise<void> {
    try {
      const logEnabled = await this.deviceLicensesService.updateLogEnableOffice();
      if (logEnabled !== null) {
        this.nativeDiagnosticLogService.setLogEnabled(logEnabled);
      }
    }
    catch (error)  {
      console.warn('checkLogEnabled', error);
    }
  }
}
